import React, { useEffect, useState } from "react";
import ToggleSwitch from "@project-stories/elements/ToggleSwitch/ToggleSwitch";
import { getAlpineStore } from "../../../../entrypoints/theme";

// Extend Window interface for TypeScript
declare global {
  interface Window {
    __completeFilters?: any[];
  }
}

export const FiltersSidebar = ({
  isVisible,
  filters,
  metaobject_filters,
  toggleSidebar,
}) => {
  const store = getAlpineStore("modelview");

  const [activeFilters, setActiveFilters] = useState([]);
  const [modelView, setModelView] = useState(store.isModelView);

  // Debug: Log filters whenever they change
  // React.useEffect(() => {
  //   console.log('FiltersSidebar received filters:', {
  //     filtersCount: filters?.length || 0,
  //     filters: filters,
  //     colorFilter: filters?.find(f => f.key === 'named_tags.swatch')
  //   });
  // }, [filters]);

  useEffect(() => {
    setModelView(store.isModelView);

    // Initialize active filters from URL on component mount
    const initializeFiltersFromURL = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const filterString = urlParams.get('filters');
      
      if (filterString) {
        try {
          const decodedParams = decodeURIComponent(filterString);
          const extractedFilters: string[] = [];
          
          // Split by AND to get different categories
          const categoryGroups = decodedParams.split(' AND ');
          
          categoryGroups.forEach(group => {
            // Remove parentheses if present
            const cleanGroup = group.replace(/^\(|\)$/g, '');
            
            // Split by OR to get values within the same category
            const orFilters = cleanGroup.split(' OR ');
            
            orFilters.forEach(filter => {
              const [key, value] = filter.split(':');
              if (value) {
                // Remove quotes from value if present
                const cleanValue = value.replace(/^'|'$/g, '');
                extractedFilters.push(cleanValue);
              }
            });
          });
          
          setActiveFilters(extractedFilters);
        } catch (error) {
          console.error('Error parsing filters from URL:', error);
        }
      } else {
        setActiveFilters([]);
      }
    };

    // Initialize filters from URL
    initializeFiltersFromURL();

    const handleActiveFilters = (event) => {
      if (event.detail.activeFilters && Object.keys(event.detail.activeFilters).length > 0) {
        const values = Object.values(event.detail.activeFilters).flat().filter(Boolean);
        setActiveFilters(values);
      } else {
        // Handle case when activeFilters is cleared/empty
        setActiveFilters([]);
      }
    };

    // Handle URL changes (browser back/forward)
    const handleUrlChange = () => {
      initializeFiltersFromURL();
    };

    // Handle page visibility changes (when returning to tab/page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        initializeFiltersFromURL();
      }
    };

    // Handle filter clear events
    const handleFilterClear = () => {
      setActiveFilters([]);
      // Force a refresh of filters to show all options
      const currentPath = window.location.pathname;
      const isCollectionPage = currentPath.includes('/collections/');
      
      if (isCollectionPage) {
        const pathParts = currentPath.split('/');
        const collectionIndex = pathParts.indexOf('collections');
        const collectionHandle = pathParts[collectionIndex + 1];
        
        if (collectionHandle) {
          const defaultSort = (window as any).defaultSort || 'default';
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('searchrequest', {
              detail: {
                type: 'plp',
                index: defaultSort,
                filters: `collections:${collectionHandle}`,
                refreshFilters: true
              }
            }));
          }, 100); // Small delay to ensure clear operation completes
        }
      }
    };

    window.addEventListener("active-filters", handleActiveFilters);
    window.addEventListener("popstate", handleUrlChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Listen for custom clear events that might be dispatched
    window.addEventListener("filters-cleared", handleFilterClear);

    // Cleanup the event listeners on component unmount
    return () => {
      window.removeEventListener("active-filters", handleActiveFilters);
      window.removeEventListener("popstate", handleUrlChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("filters-cleared", handleFilterClear);
    };
  }, []);

  // Only refresh active filters state when sidebar becomes visible
  // Don't trigger a full page reload unless absolutely necessary
  useEffect(() => {
    if (isVisible) {
      const urlParams = new URLSearchParams(window.location.search);
      const filterString = urlParams.get('filters');
      
      if (filterString) {
        try {
          const decodedParams = decodeURIComponent(filterString);
          const extractedFilters: string[] = [];
          
          // Split by AND to get different categories
          const categoryGroups = decodedParams.split(' AND ');
          
          categoryGroups.forEach(group => {
            // Remove parentheses if present
            const cleanGroup = group.replace(/^\(|\)$/g, '');
            
            // Split by OR to get values within the same category
            const orFilters = cleanGroup.split(' OR ');
            
            orFilters.forEach(filter => {
              const [key, value] = filter.split(':');
              if (value) {
                // Remove quotes from value if present
                const cleanValue = value.replace(/^'|'$/g, '');
                extractedFilters.push(cleanValue);
              }
            });
          });
          
          setActiveFilters(extractedFilters);
        } catch (error) {
          console.error('Error parsing filters from URL:', error);
        }
      } else {
        setActiveFilters([]);
      }

      // Note: Removed automatic search request dispatch to prevent page reloads
      // The filter data should already be current from the main page state
      // Only trigger searches when filters are actually changed, not when sidebar opens
    }
  }, [isVisible]);

  const handleToggleChange = (isChecked) => {
    window.dispatchEvent(
      new CustomEvent("modelview-toggle", {
        detail: { isModelView: isChecked },
      }),
    );
  };

  const FilterList = ({ filter, metaobjectFilter }) => (
    <div
      className="pb-5 mb-5 border-b border-[#eceeef] pointer-events-auto"
      style={{ order: metaobjectFilter.sort_order }}
    >
      <div className="font-medium uppercase bc">
        {metaobjectFilter.display_name}
      </div>
      <div className="flex flex-col mt-4 space-y-2 filters__list">
        {filter.values.map((value) => (
          <label className="flex items-center cursor-pointer" key={value.key}>
            <input
              type="checkbox"
              name={value.key}
              defaultValue={value.key}
              x-on:click={`filter('${filter.key}', '${value.key}');`}
              defaultChecked={activeFilters.includes(value.key)}
              className={`form-checkbox cursor-pointer ${value.key}`}
            />
            <span className="ml-2 filters__list--label">{value.label}</span>
            <span className="ml-auto">{value.count}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const FilterButton = ({ filter, metaobjectFilter }) => {
    const [visibleCount, setVisibleCount] = useState(
      metaobjectFilter.display_limit,
    );
    const [showMore, setShowMore] = useState(true);
    // Filter metaobject buttons to only show ones that are available in Algolia results
    const allButtons = metaobjectFilter.filter_buttons || [];
    
    // Debug: Compare the actual values to understand the mismatch
    // console.log(`FilterButton matching debug for ${filter.key}:`, {
    //   algoliaKeys: filter.values.map(v => v.key),
    //   metaobjectButtonNames: allButtons.map(b => b.button_name),
    //   algoliaLabels: filter.values.map(v => v.label),
    //   metaobjectDisplayNames: allButtons.map(b => b.display_name)
    // });
    
    const availableButtons = allButtons.filter(button => 
      filter.values.some(value => value.key.toLowerCase() === button.button_name.toLowerCase())
    );
    
    console.log(`FilterButton for ${filter.key}:`, {
      totalButtons: allButtons.length,
      availableButtons: availableButtons.length,
      algoliaValues: filter.values.length,
      filteredButtons: availableButtons.map(b => b.button_name)
    });
    
    // Extract current filter values for checking active state
    const currentFilterValues = filter.values.map((value) => value.key.toLowerCase());

    const handleShowMore = () => {
      setVisibleCount(
        (prevCount) => prevCount + metaobjectFilter.display_limit,
      );
      setShowMore(false);
    };

    const handleShowLess = () => {
      setVisibleCount(metaobjectFilter.display_limit);
      setShowMore(true);
    };

    const visibleButtons = availableButtons.slice(0, visibleCount);
    const hasMore = visibleCount < availableButtons.length;

    return (
      <div
        className="flex flex-col pb-5 mb-5 border-b border-[#eceeef]  pointer-events-auto"
        style={{ order: metaobjectFilter.sort_order }}
      >
        <div className="font-medium uppercase bc">
          {metaobjectFilter.display_name}
        </div>
        <div className="grid items-baseline grid-cols-4 mt-4 gap-x-3 gap-y-5">
          {visibleButtons.map((value) => (
            <label
              className="filters__button relative cursor-pointer flex flex-col items-center justify-center text-center max-w-[60px] w-full"
              key={value.button_name}
              x-on:click={`filter('${filter.key}', '${value.button_name}');`}
            >
              <input
                type="checkbox"
                defaultValue={value.button_name}
                defaultChecked={activeFilters.includes(value.button_name)}
                className={`absolute opacity-0 cursor-pointer ${value.button_name}`}
              />
              <div className="p-1 border border-transparent filters__button--wrapper">
                {value.colour ? (
                  <div
                    className="w-full h-full filters__button--item"
                    style={{ backgroundColor: value.colour }}
                  ></div>
                ) : (
                  <img
                    className="filters__button--item"
                    loading="lazy"
                    src={value.image}
                    alt={value.image.alt}
                    width="120"
                    height="auto"
                  />
                )}
              </div>
              <p className="filters__button--label b3">{value.display_name}</p>
            </label>
          ))}
        </div>
        {hasMore ? (
          <button className="mt-4 filters__show-hide" onClick={handleShowMore}>
            {`Show All ${metaobjectFilter.display_name}s (${filter.values.length})`}
          </button>
        ) : !showMore ? (
          <button className="mt-4 filters__show-hide" onClick={handleShowLess}>
            {`Show Less`}
          </button>
        ) : null}
      </div>
    );
  };

  const FilterColour = ({ filter }) => (
    <div className="pb-5 mb-5 border-b border-[#eceeef]  pointer-events-auto">
      <div className="font-medium uppercase bc">{filter.label}</div>
      <div className="flex flex-wrap mt-4 gap-x-3 gap-y-5">
        {filter.values.map((value) => (
          <label
            className="filters__colour relative cursor-pointer flex flex-col items-center justify-center text-center max-w-[60px] h-full w-full"
            key={value.key}
            x-on:click={`filter('${filter.key}', '${value.key}');`}
          >
            <input
              type="checkbox"
              defaultValue={value.key}
              defaultChecked={activeFilters.includes(value.key)}
              className="absolute opacity-0 cursor-pointer"
            />
            <div className="p-1 border border-transparent filters__colour--wrapper ">
              <div
                className={`filters__colour--item filters__colour--${value.label.toLowerCase()} w-full h-full`}
              ></div>
            </div>
            <p className="b3">{value.label}</p>
          </label>
        ))}
      </div>
    </div>
  );

  const FilterSize = ({ filter, metaobjectFilter }) => {
    // console.log('FilterSize component received data:', {
    //   filterKey: filter.key,
    //   totalValues: filter.values.length,
    //   values: filter.values,
    //   metaobjectDisplayLimit: metaobjectFilter.display_limit,
    //   metaobjectFilterName: metaobjectFilter.filter_name
    // });
    
    // Function to intelligently compare and sort size labels
    const compareSizes = (sizeA, sizeB) => {
      const letterSizeOrder = {
        'XXXS': 1, 'XXS': 2, 'XS': 3, 'S': 4, 'M': 5, 'MTM': 6, 'L': 7, 
        'XL': 8, '2XL': 9, '3XL': 10, '4XL': 11, '5XL': 12, '6XL': 13,
        'XXL': 9, 'XXXL': 10, 'XXXX': 11 // Handle alternative naming
      };
      
      const specialSizeOrder = {
        'Onesize': 1, 'OSFA': 2, 'MTM': 3
      };

      const categorizeSize = (size) => {
        const upperSize = size.toString().toUpperCase();

        if (specialSizeOrder[upperSize] || upperSize.includes('ONESIZE') || upperSize.includes('OSFA')) {
          return { category: 'special', order: specialSizeOrder[upperSize] || 999, originalSize: size };
        }

        if (letterSizeOrder[upperSize]) {
          return { category: 'letter', order: letterSizeOrder[upperSize], originalSize: size };
        }

        const numMatch = size.toString().match(/^(\d+)$/);
        if (numMatch) {
          return { category: 'numeric', order: parseInt(numMatch[1]), originalSize: size };
        }
        
        // Check for combined sizes like "40/34", "38/32"
        const combinedMatch = size.toString().match(/^(\d+)\/(\d+)$/);
        if (combinedMatch) {
          const primarySize = parseInt(combinedMatch[1]);
          const secondarySize = parseInt(combinedMatch[2]);
          return { category: 'combined', order: primarySize, secondary: secondarySize, originalSize: size };
        }
        
        // Check for sizes with letters and numbers like "2XL", "3XL"
        const letterNumMatch = size.toString().match(/^(\d+)([A-Z]+)$/);
        if (letterNumMatch) {
          const num = parseInt(letterNumMatch[1]);
          const letter = letterNumMatch[2];
          const baseOrder = letterSizeOrder[letter] || 999;
          return { category: 'letter', order: baseOrder + (num - 1) * 0.1, originalSize: size };
        }

        return { category: 'other', order: 0, originalSize: size };
      };
      
      const catA = categorizeSize(sizeA);
      const catB = categorizeSize(sizeB);
      
      // Define category priority: letter < numeric < combined < special < other
      const categoryPriority = { 'letter': 1, 'numeric': 2, 'combined': 3, 'special': 4, 'other': 5 };
      
      // First sort by category
      if (categoryPriority[catA.category] !== categoryPriority[catB.category]) {
        return categoryPriority[catA.category] - categoryPriority[catB.category];
      }
      
      // Within the same category, sort by order
      if (catA.order !== catB.order) {
        return catA.order - catB.order;
      }
      
      // For combined sizes, if primary is same, sort by secondary
      if (catA.category === 'combined' && catB.category === 'combined' && catA.secondary !== catB.secondary) {
        return (catA.secondary || 0) - (catB.secondary || 0);
      }
      
      // Fallback to string comparison
      return catA.originalSize.toString().localeCompare(catB.originalSize.toString());
    };
    
    // Function to merge current filter values with complete facets to show all sizes
    const getMergedSizeOptions = () => {
      // Get complete facets from window (set by our getCompleteFacets function)
      const completeFilters = window.__completeFilters;
      
      if (!completeFilters) {
        return filter.values.map(value => ({
          ...value,
          available: true
        }));
      }
      
      // Find the complete size filter
      const completeSizeFilter = completeFilters.find(f => f.key === 'options.size');
      
      if (!completeSizeFilter || !completeSizeFilter.values) {
        return filter.values.map(value => ({
          ...value,
          available: true
        }));
      }

      const currentAvailableSizes = new Map();
      filter.values.forEach(value => {
        currentAvailableSizes.set(value.key, value);
      });

      const mergedSizes = completeSizeFilter.values.map(completeValue => {
        const currentValue = currentAvailableSizes.get(completeValue.key);
        return {
          key: completeValue.key,
          label: completeValue.label,
          count: currentValue ? currentValue.count : 0,
          available: !!currentValue
        };
      });
      
      // Sort sizes with intelligent size ordering
      return mergedSizes.sort((a, b) => {
        // First, sort by availability (available sizes first)
        if (a.available !== b.available) {
          return b.available ? 1 : -1; // Available sizes first
        }

        return compareSizes(a.label, b.label);
      });
    };
    
    const mergedSizeOptions = getMergedSizeOptions();
    
    const displayLimit = metaobjectFilter.display_limit
      ? metaobjectFilter.display_limit
      : mergedSizeOptions.length;
    
    // Override display limit for size filters to show more options by default
    const initialDisplayLimit = metaobjectFilter.filter_name === 'size' 
      ? Math.max(displayLimit, 12) // Show more sizes by default
      : displayLimit;
    
    const [visibleCount, setVisibleCount] = useState(initialDisplayLimit);
    const [showMore, setShowMore] = useState(true);

    const handleShowMore = () => {
      setVisibleCount((prevCount) => prevCount + displayLimit);
      setShowMore(false);
    };

    const handleShowLess = () => {
      setVisibleCount(initialDisplayLimit);
      setShowMore(true);
    };

    const visibleFilters = mergedSizeOptions.slice(0, visibleCount);
    const hasMore = visibleCount < mergedSizeOptions.length;

    return (
      <div
        className="flex flex-col pb-5 mb-5 border-b border-[#eceeef]  pointer-events-auto"
        style={{ order: metaobjectFilter.sort_order }}
      >
        <div className="font-medium uppercase bc">
          {metaobjectFilter.display_name}
        </div>
        <div className="grid grid-cols-4 mt-4 gap-x-4 gap-y-4">
          {visibleFilters.map((value) => (
            <div
              className={`relative w-full filters__size ${
                value.available ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
              }`}
              key={value.key}
              x-on:click={value.available ? `filter('${filter.key}', '${value.key}')` : ''}
            >
              <input
                type="checkbox"
                defaultValue={value.key}
                defaultChecked={activeFilters.includes(value.key)}
                disabled={!value.available}
                className={`absolute opacity-0 ${value.key}`}
              />
              <button 
                className={`filters__size--button px-0 capitalize w-full h-10 flex items-center justify-center border ${
                  value.available 
                    ? 'text-[#00263E] bg-[#ECEBEB] hover:bg-[#D0D0D0]' 
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
                disabled={!value.available}
                type="button"
                tabIndex={-1}
              >
                {value.label}
                {!value.available && (
                  <span className="ml-1 text-xs opacity-60">(0)</span>
                )}
              </button>
            </div>
          ))}
        </div>
        {hasMore ? (
          <button className="mt-4 filters__show-hide" onClick={handleShowMore}>
            {`Show All ${metaobjectFilter.display_name}s (${mergedSizeOptions.length})`}
          </button>
        ) : !showMore ? (
          <button className="mt-4 filters__show-hide" onClick={handleShowLess}>
            {`Show Less`}
          </button>
        ) : null}
      </div>
    );
  };

  const renderFilterValues = (filter, metaobjectFilters) => {
    const metaobjectFilter = metaobjectFilters.find(
      (value) => value.filter_name.toLowerCase() === filter.key.toLowerCase(),
    );

    if (
      filter.key === "named_tags.swatch" ||
      filter.key === "named_tags.fabrtype"
    ) {
      return metaobjectFilter ? (
        <FilterButton filter={filter} metaobjectFilter={metaobjectFilter} />
      ) : (
        <FilterColour filter={filter} />
      );
    } else if (filter.key === "options.size") {
      return metaobjectFilter ? (
        <FilterSize filter={filter} metaobjectFilter={metaobjectFilter} />
      ) : (
        <FilterSize
          filter={filter}
          metaobjectFilter={{ sort_order: 0, display_name: filter.label }}
        />
      );
    } else {
      return metaobjectFilter ? (
        <FilterList filter={filter} metaobjectFilter={metaobjectFilter} />
      ) : (
        <FilterList
          filter={filter}
          metaobjectFilter={{ sort_order: 0, display_name: filter.label }}
        />
      );
    }
  };

  return (
    <>
      <div
        className={`fixed top-0 left-0 w-full h-full bg-black z-50 opacity-20  ${isVisible ? "" : "hidden"}`}
        onClick={toggleSidebar}
      ></div>
      <div
        className={`collection-sidebar fixed top-0 left-0 w-full md:w-[350px] h-full bg-white shadow-lg transition-transform z-50 transform ${isVisible ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div
          className={
            "facets relative flex flex-col pt-6 lg:pt-[40px] px-4 top-0 left-0 overflow-y-auto h-[calc(100dvh-70px)]"
          }
        >
          <button
            type="button"
            className="absolute top-5 right-5 px-0 h-auto min-h-[auto] lg:self-end w-auto pointer-events-auto"
            onClick={toggleSidebar}
          >
            <span className="sr-only">Close</span>
            <span className="icon-close-rounded text-[20px] text-[#54565A]"></span>
          </button>
          {/* Model View Toggle */}
          <div className="lg:hidden items-center mb-[18px]">
            <ToggleSwitch
              label={"Model View"}
              onChange={handleToggleChange}
              initialChecked={modelView}
            />
          </div>
          {filters && filters.length > 0 ? (
            filters.map((filter) => (
              <React.Fragment key={filter.key}>
                {renderFilterValues(filter, metaobject_filters)}
                {/* <hr className={'my-[18px]'} /> */}
              </React.Fragment>
            ))
          ) : (
            <div className="py-4 text-center text-gray-500">
              No filters available
            </div>
          )}
        </div>

        <div className="text-center p-4 border-t border-[#BBBBBD] w-full bg-white fixed bottom-0 flex gap-x-4 pointer-events-auto">
          <button className="w-full btn-outline" x-on:click={`clear`}>
            CLEAR FILTER
          </button>
          <button
            className="w-full btn-primary md:hidden"
            onClick={toggleSidebar}
          >
            See Results
          </button>
        </div>
      </div>
    </>
  );
};

export default FiltersSidebar;
